import { LevelType } from 'data/types';
import { findStructureItemById } from 'utils/structure';
import {
  isExplicitSaveRejection,
  STRUCTURE_SAVE_FAILURE_LIMIT
} from 'utils/structure-autosave';
import {
  StructureSaveFailureDescription,
  StructureSaveFailureInput,
  StructureSaveRejection
} from 'utils/structure-save-error-types';

export const BLOCKING_SAVE_FAILURE_TOAST = {
  id: 'structure-save-blocked',
  duration: 10000
};

const GENERIC_SAVE_ERROR_MESSAGE = 'Error saving structure';
const POST_SAVE_REFRESH_FAILURE_MESSAGE =
  'Structure saved, but its sizing details could not be updated.';
const MAX_NAMED_ITEMS = 3;
const INDEXED_FIELD_PATTERN = /^[A-Za-z_]+\[(\d+)\]\.?(.*)$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseField(
  field: unknown
): Pick<StructureSaveRejection, 'itemIndex' | 'path'> {
  if (typeof field !== 'string') {
    return { itemIndex: null, path: '' };
  }
  const match = INDEXED_FIELD_PATTERN.exec(field);
  if (!match) {
    return { itemIndex: null, path: field };
  }
  return { itemIndex: Number(match[1]), path: match[2] };
}

export function parseStructureSaveRejections(
  error: unknown
): StructureSaveRejection[] {
  if (!isRecord(error) || !isRecord(error.data)) {
    return [];
  }
  const { errors } = error.data;
  if (!Array.isArray(errors)) {
    return [];
  }
  return errors.filter(isRecord).map((issue) => ({
    ...parseField(issue.field),
    message: typeof issue.message === 'string' ? issue.message : 'Invalid'
  }));
}

function labelFor(structure: LevelType[], uniqueId: string): string {
  const item = findStructureItemById(structure, uniqueId);
  return item?.label?.trim() || uniqueId;
}

function rejectionLabel(
  rejection: StructureSaveRejection,
  input: StructureSaveFailureInput
): string | null {
  if (rejection.itemIndex === null) {
    return null;
  }
  const uniqueId = input.failedIds[rejection.itemIndex];
  return uniqueId ? labelFor(input.structure, uniqueId) : null;
}

function joinLabels(labels: string[]): string {
  const unique = [...new Set(labels)];
  if (unique.length <= MAX_NAMED_ITEMS) {
    return unique.join(', ');
  }
  const named = unique.slice(0, MAX_NAMED_ITEMS).join(', ');
  return `${named} and ${unique.length - MAX_NAMED_ITEMS} more`;
}

function uniqueLabels(structure: LevelType[], uniqueIds: string[]): string[] {
  return [...new Set(uniqueIds.map((id) => labelFor(structure, id)))];
}

function notSavedSuffix(labels: string[]): string {
  return new Set(labels).size === 1
    ? 'Its changes are not being saved.'
    : 'Their changes are not being saved.';
}

function unsavedScopeSuffix(
  scopeLabels: string[],
  namedLabels: string[]
): string {
  const everyItemNamed = scopeLabels.every((label) =>
    namedLabels.includes(label)
  );
  return everyItemNamed
    ? notSavedSuffix(scopeLabels)
    : `Changes to ${joinLabels(scopeLabels)} are not being saved.`;
}

function describeExplicitRejection(
  input: StructureSaveFailureInput
): StructureSaveFailureDescription {
  const rejections = parseStructureSaveRejections(input.error);
  const details = rejections.map((rejection) => {
    const label = rejectionLabel(rejection, input) ?? 'Structure';
    return `${label}: ${rejection.path || 'request'} (${rejection.message})`;
  });
  const rejectedLabels = rejections
    .map((rejection) => rejectionLabel(rejection, input))
    .filter((label): label is string => label !== null);
  const scopeLabels = uniqueLabels(input.structure, input.failedIds);
  const moreIssues =
    details.length > 1 ? ` and ${details.length - 1} more issue(s)` : '';
  const reason =
    details.length > 0
      ? `${details[0]}${moreIssues}`
      : `${joinLabels(scopeLabels)}: the server rejected the change`;
  const namedLabels = details.length > 0 ? rejectedLabels : scopeLabels;

  return {
    message: `Could not save ${reason}. ${unsavedScopeSuffix(
      scopeLabels,
      namedLabels
    )}`,
    details,
    blocking: true
  };
}

function describeQuarantine(
  input: StructureSaveFailureInput
): StructureSaveFailureDescription {
  const labels = input.quarantinedIds.map((id) =>
    labelFor(input.structure, id)
  );
  return {
    message: `Could not save ${joinLabels(
      labels
    )} after ${STRUCTURE_SAVE_FAILURE_LIMIT} attempts. ${notSavedSuffix(
      labels
    )}`,
    details: [],
    blocking: true
  };
}

export function describeStructureSaveFailure(
  input: StructureSaveFailureInput
): StructureSaveFailureDescription {
  if (input.failedIds.length === 0) {
    return {
      message: POST_SAVE_REFRESH_FAILURE_MESSAGE,
      details: [],
      blocking: false
    };
  }
  if (isExplicitSaveRejection(input.error)) {
    return describeExplicitRejection(input);
  }
  if (input.quarantinedIds.length > 0) {
    return describeQuarantine(input);
  }
  return { message: GENERIC_SAVE_ERROR_MESSAGE, details: [], blocking: false };
}
